// Persistence backend implementations for JS environments.
import type { PersistenceBackend } from './types.js';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

// Binary-safe base64 encoding/decoding that handles all byte values 0-255.
// btoa(String.fromCharCode(...)) can fail for large arrays (stack overflow)
// and atob can produce incorrect results for certain byte sequences.
const B64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

function base64Encode(bytes: Uint8Array): string {
  let result = '';
  const len = bytes.length;
  for (let i = 0; i < len; i += 3) {
    const b0 = bytes[i]!;
    const b1 = i + 1 < len ? bytes[i + 1]! : 0;
    const b2 = i + 2 < len ? bytes[i + 2]! : 0;
    result += B64_CHARS[(b0 >> 2)!]!;
    result += B64_CHARS[((b0 & 0x03) << 4) | (b1 >> 4)]!;
    result += i + 1 < len ? B64_CHARS[((b1 & 0x0f) << 2) | (b2 >> 6)]! : '=';
    result += i + 2 < len ? B64_CHARS[b2 & 0x3f]! : '=';
  }
  return result;
}

function base64Decode(str: string): Uint8Array {
  // Build reverse lookup
  const lookup = new Uint8Array(128);
  for (let i = 0; i < B64_CHARS.length; i++) {
    lookup[B64_CHARS.charCodeAt(i)] = i;
  }

  // Strip padding
  let end = str.length;
  while (end > 0 && str[end - 1] === '=') end--;

  const out = new Uint8Array(Math.floor((end * 3) / 4));
  let j = 0;
  for (let i = 0; i < end; i += 4) {
    const c0 = lookup[str.charCodeAt(i)]!;
    const c1 = i + 1 < end ? lookup[str.charCodeAt(i + 1)]! : 0;
    const c2 = i + 2 < end ? lookup[str.charCodeAt(i + 2)]! : 0;
    const c3 = i + 3 < end ? lookup[str.charCodeAt(i + 3)]! : 0;
    out[j++] = (c0 << 2) | (c1 >> 4);
    if (i + 2 < end) out[j++] = ((c1 & 0x0f) << 4) | (c2 >> 2);
    if (i + 3 < end) out[j++] = ((c2 & 0x03) << 6) | c3;
  }
  return out.subarray(0, j);
}

/**
 * localStorage-backed persistence for browser environments.
 * Keys are namespaced with a prefix to avoid collisions.
 */
export class LocalStoragePersistence implements PersistenceBackend {
  private readonly prefix: string;

  constructor(appId: string) {
    this.prefix = `layers_sdk_${appId}_`;
  }

  write(key: string, data: Uint8Array): void {
    try {
      const encoded = base64Encode(data);
      window.localStorage.setItem(this.prefix + key, encoded);
    } catch {
      // localStorage full or unavailable — silently drop
    }
  }

  read(key: string): Uint8Array | null {
    try {
      const raw = window.localStorage.getItem(this.prefix + key);
      if (raw === null) return null;
      return base64Decode(raw);
    } catch {
      return null;
    }
  }

  delete(key: string): void {
    try {
      window.localStorage.removeItem(this.prefix + key);
    } catch {
      // Ignore
    }
  }

  listKeys(prefix: string): string[] {
    const keys: string[] = [];
    try {
      const fullPrefix = this.prefix + prefix;
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k?.startsWith(fullPrefix)) {
          keys.push(k.slice(this.prefix.length));
        }
      }
    } catch {
      // Ignore
    }
    keys.sort();
    return keys;
  }
}

/**
 * In-memory persistence for environments without localStorage (SSR, tests).
 */
export class MemoryPersistence implements PersistenceBackend {
  /** Durability class, reported by the `$sdk_health` beacon. Not minified away. */
  readonly __layersBackend = 'memory';

  private readonly store = new Map<string, Uint8Array>();

  write(key: string, data: Uint8Array): void {
    this.store.set(key, new Uint8Array(data));
  }

  read(key: string): Uint8Array | null {
    return this.store.get(key) ?? null;
  }

  delete(key: string): void {
    this.store.delete(key);
  }

  listKeys(prefix: string): string[] {
    const keys: string[] = [];
    for (const k of this.store.keys()) {
      if (k.startsWith(prefix)) {
        keys.push(k);
      }
    }
    keys.sort();
    return keys;
  }
}

/**
 * No-op persistence — events only live in the in-memory queue.
 */
export class NullPersistence implements PersistenceBackend {
  /** Durability class, reported by the `$sdk_health` beacon. Not minified away. */
  readonly __layersBackend = 'null';

  write(_key: string, _data: Uint8Array): void {}
  read(_key: string): Uint8Array | null {
    return null;
  }
  delete(_key: string): void {}
  listKeys(_prefix: string): string[] {
    return [];
  }
}

/**
 * Detect and return the best persistence backend for the current environment.
 */
export function createDefaultPersistence(appId: string): PersistenceBackend {
  if (typeof window !== 'undefined') {
    try {
      const test = '__layers_test__';
      window.localStorage.setItem(test, test);
      window.localStorage.removeItem(test);
      return new LocalStoragePersistence(appId);
    } catch {
      // localStorage unavailable
    }
  }
  return new NullPersistence();
}
